import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DEFAULT_PAGINATED_DROPDOWN_PAGE_SIZE,
  PaginatedDropdownConfig,
  PaginatedDropdownLoadParams,
  PaginatedOption,
  PaginatedOptionsPage,
  mapPageContentToPaginatedPage,
} from './paginated-dropdown.util';

/**
 * Build {@link PaginatedDropdownConfig} with the standard SPU material-name UX:
 * load page 1 on open, append pages on scroll-to-end until API has no more data.
 */
export type { PaginatedDropdownConfig } from './paginated-dropdown.util';

export function createStandardPaginatedLoader(
  loadPage: PaginatedDropdownConfig['loadPage'],
  options?: Partial<Omit<PaginatedDropdownConfig, 'loadPage'>>,
): PaginatedDropdownConfig {
  return {
    pageSize: DEFAULT_PAGINATED_DROPDOWN_PAGE_SIZE,
    debounceMs: 300,
    ...options,
    loadPage,
  };
}
import type { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import type { FormField } from '@shared/components/add-form/add-form.component';

/** Client-side slice + filter for static/JSON option lists (provinces, wards, …). */
export function createClientSidePaginatedLoader<V = string | number>(
  getAllOptions: () => Observable<PaginatedOption<V>[]>,
  filterFn?: (option: PaginatedOption<V>, searchText: string) => boolean,
): PaginatedDropdownConfig<V>['loadPage'] {
  return (params) =>
    getAllOptions().pipe(
      map((allOptions) => {
        const searchText = params.searchText.trim().toLowerCase();
        const filtered = searchText
          ? allOptions.filter((option) =>
              filterFn
                ? filterFn(option, searchText)
                : option.label.toLowerCase().includes(searchText),
            )
          : allOptions;

        const start = (params.page - 1) * params.size;
        const pageOptions = filtered.slice(start, start + params.size);

        return {
          options: pageOptions,
          pageContentLength: pageOptions.length,
          totalElements: filtered.length,
        };
      }),
    );
}

export function createPageContentPaginatedLoader<T, V = string | number>(
  fetchPage: (request: {
    page: number;
    size: number;
    searchText: string;
  }) => Observable<{
    pageContent?: T[];
    content?: T[];
    totalElements?: number;
    total?: number;
    hasNextPage?: boolean;
  } | null | undefined>,
  mapItem: (item: T) => PaginatedOption<V>,
  options?: { apiPageZeroBased?: boolean },
): PaginatedDropdownConfig<V>['loadPage'] {
  return (params: PaginatedDropdownLoadParams) => {
    const page = options?.apiPageZeroBased ? params.page - 1 : params.page;
    return fetchPage({
      page,
      size: params.size,
      searchText: params.searchText,
    }).pipe(
      map((response) => mapPageContentToPaginatedPage(response, mapItem)),
    );
  };
}

export function withPaginatedSearchFields(
  fields: FormFieldConfig[],
  loaders: Record<string, PaginatedDropdownConfig>,
): FormFieldConfig[] {
  return fields.map((field) => {
    const loader = loaders[field.key];
    if (!loader) {
      return field;
    }

    const isMultiselect = field.type === 'multiselect';
    return {
      ...field,
      type: isMultiselect ? 'multiselect' : 'searchableDropdown',
      showClear: field.showClear ?? true,
      paginatedOptions: loader,
    };
  });
}

export function withPaginatedFormFields(
  fields: FormField[],
  loaders: Record<string, PaginatedDropdownConfig>,
): FormField[] {
  return fields.map((field) => {
    const loader = loaders[field.controlName];
    if (!loader) {
      return field;
    }

    return {
      ...field,
      type: 'searchableDropdown',
      showClear: field.showClear ?? true,
      paginatedOptions: loader,
    };
  });
}
